import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Anchor,
  ArrowDownIcon,
  ArrowUpIcon,
  Box,
  Checkbox,
  costFormater,
  NewTabIcon,
  RawButton,
  Text,
  useResponsive,
} from '@common-fe/common-fe';
import _ from 'lodash';
import styled, { css } from 'styled-components';

import { useGetFundIcon } from '../../hooks';
import { InvestmentHoldingStatus } from '../../investments.types';
import useInstrumentCategoriesQuery from '../queries/useInstrumentCategories.query';
import { FundPayload } from '../SelfDirected.types';

import FundAllocationInput from './FundAllocationInput';
interface WrapperProps {
  isHighlighted?: boolean;
}
const Wrapper = styled(Box)<WrapperProps>`
  transition: all 0.25s;
  &:hover {
    ${({ theme }) => theme.shadows.hover};
  }
  ${({ isHighlighted }) => {
    if (isHighlighted) {
      return css`
        animation-name: showHighlight;
        animation-duration: 3.5s;
        @keyframes showHighlight {
          0% {
            border: 2px solid ${({ theme }) => theme.colors.accentActive};
          }
          75% {
            border: 2px solid ${({ theme }) => theme.colors.accentActive};
          }
          100% {
            border-color: 1px solid ${({ theme }) => theme.colors.border1};
          }
        }
      `;
    }

    return css``;
  }}
`;

const EMPTY_SIGN = '-';
const EMPTY_SHARES_SIGN = '0.000';

const EMPTY_AMOUNT_SIGN = '$0.00';

interface Props extends FundPayload {
  checked?: boolean;
  onSelect: (value: string) => void;
  disabled?: boolean;
  disabledAllocation?: boolean;
  isInvestMoreMode?: boolean;
  investedAmount: number;
  isOnlyOneAvailable?: boolean;
  value: number;
  onChangeValue: (value: number) => void;
  isMinified?: boolean;
}

const SelfDirectedAllocationItem: React.FC<Props> = ({
  value,
  status,
  onChangeValue,
  isInvestMoreMode,
  logo,
  title,
  stockCode,
  link,
  assetClass,
  disabled,
  isOnlyOneAvailable,
  onSelect,
  id,
  checked,
  investedAmount,
  price = 0,
  category,
  isMinified,
  isHighlighted,
  disabledAllocation,
}) => {
  const { isMobile } = useResponsive();
  const { t } = useTranslation();
  const { dataMap } = useInstrumentCategoriesQuery();
  const currentCategory = useMemo(() => _.get(dataMap, `${[category]}`, '') as string, [category, dataMap]);

  const currentAmount = useMemo(() => {
    if (value !== undefined && !investedAmount) {
      return EMPTY_AMOUNT_SIGN;
    }
    const displayedValue = investedAmount * (value / 100);

    return _.isNaN(displayedValue) ? EMPTY_AMOUNT_SIGN : displayedValue;
  }, [investedAmount, value]);

  const sharesAmount = useMemo(() => {
    if (_.isNumber(currentAmount)) {
      return currentAmount / price;
    }
    return EMPTY_SIGN;
  }, [currentAmount, price]);
  const handleGetFundIcon = useGetFundIcon();

  const [expanded, setExpanded] = useState(false);
  const handleToggleExpanded = useCallback(() => {
    setExpanded(!expanded);
  }, [expanded]);
  const isDisabledApplied = useMemo(() => {
    if (isInvestMoreMode) {
      return disabled || status === InvestmentHoldingStatus.Inactive;
    }
    return disabled;
  }, [disabled, isInvestMoreMode, status]);
  const isGreyMode = useMemo(() => {
    return !!(isDisabledApplied && investedAmount);
  }, [isDisabledApplied, investedAmount]);
  const isForceDisabled = useMemo(() => {
    return isInvestMoreMode && status === InvestmentHoldingStatus.Inactive;
  }, [isInvestMoreMode, status]);
  const isGreyApplied = useMemo(() => {
    return isGreyMode || !checked;
  }, [checked, isGreyMode]);
  if (isMobile) {
    return (
      <Wrapper
        data-testid={`fund-item_${id}`}
        direction="column"
        pad={isMobile ? 'spacing12' : { vertical: '17px', horizontal: 'spacing24' }}
        background="canvas"
        isHighlighted={isHighlighted}
        round="container2Round"
        border={{ size: 'small', color: 'border1' }}
        margin={{ top: 'spacing8' }}
      >
        <Box direction="row" align="center">
          <div>
            <Checkbox
              testId="fund-item"
              name={`${id}_${checked ? 'checked' : 'unchecked'}`}
              checked={checked}
              disabled={isForceDisabled}
              onChange={() => {
                onSelect(id);
              }}
            />
          </div>
          <div>
            <Box margin={{ right: 'spacing12' }}>
              {handleGetFundIcon({
                image: logo,
                status: status,
                isGreyMode: isGreyApplied,
                isNullMode: true,
              })}
            </Box>
          </div>
          <Box direction="column" margin={{ right: 'spacing12' }} fill>
            <Box width="100%">
              <Text
                data-testid={`fund-item_${id}_title`}
                tip={title}
                color={isGreyApplied ? 'textDisabled' : 'textBody'}
                truncate
                weight={700}
              >
                {title}
              </Text>
              <Text
                data-testid={`fund-item_${id}_symbol`}
                color={isGreyApplied ? 'textDisabled' : 'textBody'}
              >
                {stockCode}
              </Text>

              <Box>
                <Text
                  color={isGreyMode ? 'textDisabled' : 'textBody'}
                  truncate
                  data-testid={`fund-item_${id}_category`}
                  title={currentCategory}
                >
                  {currentCategory}
                </Text>
              </Box>
            </Box>
          </Box>
          <div>
            <Box direction="column" align="end">
              <Text
                data-testid={`fund-item_${id}_current-amount`}
                weight={700}
                size="medium"
                color={
                  isGreyApplied ? 'textDisabled' : _.toNumber(currentAmount) > 0 ? 'success' : 'textBody'
                }
              >
                {_.isNumber(currentAmount)
                  ? costFormater(currentAmount, { isPrecision: true })
                  : currentAmount}
              </Text>
              <Text
                size="small"
                data-testid={`fund-item_${id}_price`}
                color={isGreyApplied ? 'textDisabled' : 'textBody'}
              >
                SP:
                {costFormater(price, {
                  isPrecision: true,
                })}
              </Text>

              <Text
                size="small"
                data-testid={`fund-item_${id}_shares`}
                color={isGreyApplied ? 'textDisabled' : 'textBody'}
              >
                S:{_.isNumber(sharesAmount) ? _.floor(sharesAmount, 4) : EMPTY_SHARES_SIGN}
              </Text>
            </Box>
          </div>
        </Box>
        <Box height="1px" background="border1" margin={{ vertical: 'spacing12' }} />
        <Box direction="row" align="center">
          <Box
            margin={{ left: 'spacing12' }}
            style={{
              width: '24px',
            }}
            direction="column"
          >
            {link ? (
              <Anchor data-testid={`fund-item_${id}_link`} target="_blank" href={link}>
                <Box align="center">
                  <NewTabIcon color="iconSecondary" size="26px" />
                </Box>
              </Anchor>
            ) : (
              <Box width="26px" />
            )}
          </Box>
          <Box margin={{ left: 'auto' }}>
            <FundAllocationInput
              testId={`fund-item_${id}`}
              // key={`${value === undefined}`}
              isEmpty={!investedAmount}
              isOnlyOneAvailable={isOnlyOneAvailable}
              disabled={disabledAllocation || isForceDisabled}
              value={value}
              onChange={onChangeValue}
            />
          </Box>
        </Box>
      </Wrapper>
    );
  }
  return (
    <Wrapper
      data-testid={`fund-item_${id}`}
      direction="column"
      pad={isMobile ? 'spacing12' : { vertical: '17px', horizontal: 'spacing24' }}
      background="canvas"
      isHighlighted={isHighlighted}
      round="container2Round"
      border={{ size: 'small', color: 'border1' }}
      margin={{ top: 'spacing8' }}
    >
      <Box direction="row" align="center">
        {!isMinified && (
          <RawButton data-testid={`fund-item_${id}_toggle-button`} onClick={handleToggleExpanded}>
            <Box width="24px" height="24px">
              {expanded ? <ArrowUpIcon color="iconPrimary" /> : <ArrowDownIcon color="iconPrimary" />}
            </Box>
          </RawButton>
        )}
        <Box margin={{ left: 'spacing12' }}>
          <Checkbox
            testId="fund-item"
            name={`${id}_${checked ? 'checked' : 'unchecked'}`}
            checked={checked}
            disabled={isForceDisabled}
            onChange={() => {
              onSelect(id);
            }}
          />
        </Box>
        <Box margin={{ left: 'spacing12' }}>
          {handleGetFundIcon({
            image: logo,
            status: status,
            isGreyMode: isGreyApplied,
            isNullMode: true,
          })}
        </Box>
        <Box
          margin={{ left: 'spacing12' }}
          style={{
            flex: 2,
            position: 'relative',
          }}
          direction="column"
        >
          <Box width="100%">
            <Text
              data-testid={`fund-item_${id}_title`}
              tip={title}
              color={isGreyApplied ? 'textDisabled' : 'textBody'}
              truncate
              weight={700}
            >
              {title}
            </Text>
            <Text data-testid={`fund-item_${id}_symbol`} color={isGreyApplied ? 'textDisabled' : 'textBody'}>
              {stockCode}
            </Text>
          </Box>
        </Box>
        <Box
          margin={{ left: 'spacing12' }}
          style={{
            width: '24px',
          }}
          direction="column"
        >
          {link ? (
            <Anchor data-testid={`fund-item_${id}_link`} target="_blank" href={link}>
              <Box align="center">
                <NewTabIcon color="iconSecondary" size="26px" />
              </Box>
            </Anchor>
          ) : (
            <Box width="26px" />
          )}
        </Box>

        <Box width="90px" margin={{ left: 'spacing24' }}>
          <Text data-testid={`fund-item_${id}_price`} color={isGreyApplied ? 'textDisabled' : 'textBody'}>
            {costFormater(price, {
              isPrecision: true,
            })}
          </Text>
        </Box>

        <Box width={{ min: '90px' }} margin={{ left: 'spacing24' }}>
          <Text data-testid={`fund-item_${id}_shares`} color={isGreyApplied ? 'textDisabled' : 'textBody'}>
            {_.isNumber(sharesAmount) ? _.floor(sharesAmount, 4) : EMPTY_SHARES_SIGN}
          </Text>
        </Box>
        <Box width={{ min: '90px' }} margin={{ left: 'spacing24' }}>
          <Text
            data-testid={`fund-item_${id}_current-amount`}
            weight={700}
            color={isGreyApplied ? 'textDisabled' : _.toNumber(currentAmount) > 0 ? 'success' : 'textBody'}
          >
            {_.isNumber(currentAmount) ? costFormater(currentAmount, { isPrecision: true }) : currentAmount}
          </Text>
        </Box>

        <Box margin={{ left: isMinified ? 'spacing24' : undefined }}>
          <FundAllocationInput
            testId={`fund-item_${id}`}
            isEmpty={!investedAmount}
            isOnlyOneAvailable={isOnlyOneAvailable}
            disabled={disabledAllocation || isForceDisabled}
            value={value}
            onChange={onChangeValue}
          />
        </Box>
      </Box>

      {expanded && (
        <>
          <Box height="1px" background="border1" margin={{ top: 'l', bottom: 'l' }}></Box>
          <Box direction="row" align="center">
            <Box direction="column" width="150px" margin={{ right: 'l' }}>
              <Text
                size="small"
                weight={700}
                color={isGreyMode ? 'textDisabled' : 'textBody'}
                margin={{ bottom: 'xxxs' }}
              >
                Category
              </Text>
              <Box pad={{ vertical: 'xxxs' }}>
                <Text
                  color={isGreyMode ? 'textDisabled' : 'textBody'}
                  truncate
                  data-testid={`fund-item_${id}_category`}
                  size="medium"
                >
                  {currentCategory}
                </Text>
              </Box>
            </Box>
            <Box direction="column" width="150px" margin={{ right: 'l' }}>
              <Text
                size="small"
                weight={700}
                color={isGreyMode ? 'textDisabled' : 'textBody'}
                margin={{ bottom: 'xxxs' }}
              >
                {t('Asset class')}
              </Text>
              <Box pad={{ vertical: 'xxxs' }}>
                <Text
                  data-testid={`fund-item_${id}_asset-class`}
                  color={isGreyMode ? 'textDisabled' : 'textBody'}
                  size="medium"
                >
                  {assetClass}
                </Text>
              </Box>
            </Box>
          </Box>
        </>
      )}
    </Wrapper>
  );
};

export default SelfDirectedAllocationItem;
